import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { UitkPagingPrevButton, UitkPagingNextButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { classNames, liveAnnounce } from 'uitk-react-utils';
import styles from './uitk-date-picker.css';
import { UitkMonth } from './uitk-month.esm.js';

var getClassNameList = classNames.bind(styles);
var l10nMonths = {
    monthNames: null
};
var Calendar = /** @class */ (function (_super) {
    __extends(Calendar, _super);
    function Calendar(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            months: [],
            renderTimers: []
        };
        if (!l10nMonths.monthNames) {
            l10nMonths.monthNames = _this.getMonthNames();
        }
        _this.prevMonth = _this.prevMonth.bind(_this);
        _this.nextMonth = _this.nextMonth.bind(_this);
        _this.calculateMonths = _this.calculateMonths.bind(_this);
        _this.calcNewStartDate = _this.calcNewStartDate.bind(_this);
        _this.setDisabledButtons = _this.setDisabledButtons.bind(_this);
        _this.contentRef = React.createRef();
        if (props.holidays && props.holidays.length > 0) {
            props.selection.configureHolidays(props.holidays);
            props.updateDatePicker();
        }
        _this.onSelectDate = _this.onSelectDate.bind(_this);
        _this.getHolidayName = _this.getHolidayName.bind(_this);
        return _this;
    }
    Calendar.prototype.componentDidMount = function () {
        var _this = this;
        var monthsToRender = this.props.monthsToRender;
        this._isMounted = true;
        window.requestAnimationFrame(function () {
            _this.calculateMonths(monthsToRender);
        });
    };
    Calendar.prototype.shouldComponentUpdate = function (nextProps, nextState) {
        var _a = this.props, endDate = _a.endDate, datePickerContent = _a.datePickerContent, selection = _a.selection, selectionEnd = _a.selectionEnd, selectionStart = _a.selectionStart, startDate = _a.startDate;
        var months = this.state.months;
        if (months !== nextState.months) {
            return true;
        }
        var selectionChanged = !(nextProps.selectionStart === selectionStart && nextProps.selectionEnd === selectionEnd);
        var requiredPropsChanged = startDate !== nextProps.startDate ||
            endDate !== nextProps.endDate ||
            selection !== nextProps.selection ||
            datePickerContent !== nextProps.datePickerContent;
        return selectionChanged || requiredPropsChanged;
    };
    Calendar.prototype.componentWillUnmount = function () {
        var renderTimers = this.state.renderTimers;
        this._isMounted = false;
        renderTimers.forEach(function (timer) {
            clearTimeout(timer);
        });
    };
    Calendar.prototype.onSelectDate = function (event, date, localizedText) {
        var _a = this.props, isDialog = _a.isDialog, onChangeEndDate = _a.onChangeEndDate, onChangeStartDate = _a.onChangeStartDate, onSelectDate = _a.onSelectDate, selection = _a.selection, singleMode = _a.singleMode, updateDatePicker = _a.updateDatePicker;
        var changeStart = selection.isSelectingStart;
        var dateText = date.toDateString();
        if (singleMode) {
            selection.singleDateSelected(date);
        }
        else {
            selection.dateSelected(date);
        }
        if (selection.isSelectingEnd === true) {
            this.announceDateInstructions(localizedText.selectEndDate(dateText), 'polite');
        }
        else {
            this.announceDateInstructions(localizedText.selectDone(dateText), 'polite');
        }
        if (isDialog && onSelectDate) {
            // Update state internally in dialog-view
            onSelectDate();
        }
        else {
            // Update state up the chain in uitk-date-picker (needed for menu)
            updateDatePicker();
        }
        if (onChangeStartDate && (changeStart || (selection.start && !selection.end))) {
            onChangeStartDate(selection.start);
        }
        else if (onChangeEndDate && selection.end) {
            onChangeEndDate(selection.end);
        }
    };
    Calendar.prototype.getHolidayName = function (date) {
        var _a = this.props, l10n = _a.l10n, selection = _a.selection, holidays = _a.holidays;
        if (selection.holidaysConfigured && selection.dayHolidays[date.toDateString()]) {
            var parsedDate_1;
            var dateString_1;
            var holidayData = holidays &&
                holidays.find(function (holiday) {
                    parsedDate_1 = l10n.parseLocalDateString(holiday.date, { dateFormat: 'iso', raw: 'yyyy-MM-dd' });
                    dateString_1 = new Date(parsedDate_1).toDateString();
                    return dateString_1 === date.toDateString();
                });
            return holidayData === null || holidayData === void 0 ? void 0 : holidayData.name;
        }
    };
    Calendar.prototype.getMonthNames = function () {
        // Use this to get month names programmatically
        var date = new Date();
        var curYear = date.getFullYear();
        var monthNames = [];
        var l10n = this.props.l10n;
        for (var i = 0; i < 12; i += 1) {
            monthNames.push(l10n.formatDate(new Date(curYear, i), { raw: 'MMMM', dateFormat: 'month-full' }));
        }
        return monthNames;
    };
    Calendar.prototype.getSelection = function (monthInfo, selection) {
        var currentMonth = monthInfo.month;
        var currentYear = monthInfo.year;
        var selectedStart = selection.start;
        var selectedEnd = selection.end;
        // Number of Months
        // 12 * year because 12 months in a year
        // plus month number
        // This makes it easier compare if a certain month is before after another month
        var currentNumOfMonths = currentYear * 12 + currentMonth;
        var selectedStartNumOfMonths = selectedStart
            ? selectedStart.getFullYear() * 12 + selectedStart.getMonth()
            : false;
        var selectedEndNumOfMonths = selectedEnd ? selectedEnd.getFullYear() * 12 + selectedEnd.getMonth() : false;
        // Does current month contain start date or end date
        if (currentNumOfMonths === selectedStartNumOfMonths || currentNumOfMonths === selectedEndNumOfMonths) {
            return selection;
        }
        // Does current month contain some of the range (and not start or end date)
        if (selectedEndNumOfMonths !== false &&
            currentNumOfMonths > selectedStartNumOfMonths &&
            currentNumOfMonths < selectedEndNumOfMonths) {
            return selection;
        }
        // If current month doesn't contain any selected days then selection object doesn't need to be passed to month
        return undefined;
    };
    Calendar.prototype.setDisabledButtons = function (months) {
        var props = this.props;
        var length = months.length;
        if (months) {
            this.isPrevDisabled =
                months[0].month === props.startDate.getMonth() && months[0].year === props.startDate.getFullYear();
            this.isNextDisabled =
                months[length - 1].month === props.endDate.getMonth() &&
                    months[length - 1].year === props.endDate.getFullYear();
        }
    };
    Calendar.prototype.nextMonth = function () {
        var _a = this.props, monthsToRender = _a.monthsToRender, onChangeMonth = _a.onChangeMonth;
        var newStartDate = this.calcNewStartDate(true);
        this.calculateMonths(monthsToRender, newStartDate, true, true);
        if (onChangeMonth) {
            onChangeMonth(newStartDate);
        }
    };
    Calendar.prototype.prevMonth = function () {
        var _a = this.props, monthsToRender = _a.monthsToRender, onChangeMonth = _a.onChangeMonth;
        var newStartDate = this.calcNewStartDate(false);
        this.calculateMonths(monthsToRender, newStartDate, false, true);
        if (onChangeMonth) {
            onChangeMonth(newStartDate);
        }
    };
    Calendar.prototype.calcNewStartDate = function (isNext) {
        var monthsToRender = this.props.monthsToRender;
        var months = this.state.months;
        var month = months;
        var lastMonth = month.length - 1;
        var totalMonthsToRender = monthsToRender || 2;
        var monthsToSubstract = isNext ? totalMonthsToRender - 2 : monthsToRender;
        return new Date(month[lastMonth].year, 
        // @ts-ignore
        month[lastMonth].month - monthsToSubstract, 1, 12);
    };
    Calendar.prototype.scrollToSelection = function () {
        /* This method uses a non-react way of scrolling to selection,
           because it is the simplest way to do it.
         */
        var selection = this.props.selection;
        var selectedClassNames = getClassNameList('uitk-date-picker-day-number', {
            endSelected: selection.isSelectingEnd && selection.end,
            startSelected: selection.isSelectingStart && selection.start
        });
        var selectedDateElemArr = document.getElementsByClassName(selectedClassNames);
        if (selectedDateElemArr.length === 1) {
            // ** Firefox IE edge
            var calendarWrapper = document.querySelector('.uitk-calendar');
            if (calendarWrapper && calendarWrapper.scrollTo) {
                var viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
                var toScroll = selectedDateElemArr[0].offsetTop - viewportHeight / 2;
                calendarWrapper.scrollTo(0, toScroll);
            }
            else if (selectedDateElemArr[0].scrollIntoViewIfNeeded) {
                selectedDateElemArr[0].scrollIntoViewIfNeeded();
            }
            else if (selectedDateElemArr[0].scrollIntoView) {
                selectedDateElemArr[0].scrollIntoView();
            }
        }
    };
    Calendar.prototype.recursivelyRenderRemainingMonths = function (months, indexOfLastRenderedMonth) {
        var _this = this;
        var renderTimers = this.state.renderTimers;
        var indexOfNextMonthToRender = indexOfLastRenderedMonth + 2;
        if (indexOfNextMonthToRender <= months.length + 1) {
            var timer = setTimeout(function () {
                _this.setState({ months: months.slice(0, indexOfNextMonthToRender) });
                _this.recursivelyRenderRemainingMonths(months, indexOfNextMonthToRender);
            }, 0);
            renderTimers.push(timer);
        }
    };
    Calendar.prototype.calculateMonths = function (max, newStartDate, nextMonth, keyPressed) {
        var _a, _b, _c;
        var maxCount = max || 48;
        var _d = this.props, endDate = _d.endDate, firstMonth = _d.firstMonth, isDialog = _d.isDialog, l10n = _d.l10n, selection = _d.selection;
        var monthOne = newStartDate || firstMonth;
        var endDateMonthYear = new Date(endDate === null || endDate === void 0 ? void 0 : endDate.getFullYear(), endDate === null || endDate === void 0 ? void 0 : endDate.getMonth());
        var selectionStartDateMonthYear = new Date((_a = selection === null || selection === void 0 ? void 0 : selection.start) === null || _a === void 0 ? void 0 : _a.getFullYear(), (_b = selection === null || selection === void 0 ? void 0 : selection.start) === null || _b === void 0 ? void 0 : _b.getMonth());
        var monthsToSubstract = 0;
        if (endDateMonthYear.getTime() === selectionStartDateMonthYear.getTime() &&
            ((_c = selection === null || selection === void 0 ? void 0 : selection.start) === null || _c === void 0 ? void 0 : _c.getTime()) >= new Date().getTime()) {
            monthsToSubstract = 1;
        }
        var currentMonth = new Date(monthOne.getFullYear(), monthOne.getMonth() - monthsToSubstract, 1, 12);
        /*  Building a model for each month by adding 1 month at a time.
            Since bookings are usually not more than 12 months in advance,
            this is acceptable.
          */
        var months = [];
        var index = 0;
        if (endDate) {
            while (endDate.getTime() - currentMonth.getTime() >= 0 && index < maxCount) {
                months.push({
                    month: currentMonth.getMonth(),
                    name: l10nMonths.monthNames[currentMonth.getMonth()],
                    year: currentMonth.getFullYear()
                });
                currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1, 12);
                index += 1;
            }
            if (isDialog) {
                this.renderUpToSelectedMonth(months);
            }
            else {
                // No need for recursion on Menu views
                this.setDisabledButtons(months);
                if (this._isMounted) {
                    this.setState({ months: months });
                    if (keyPressed) {
                        // eslint-disable-next-line max-depth
                        if (months[1]) {
                            liveAnnounce(l10n.formatText('datePicker.currentMonths.a11yLabel', "".concat(months[0].name, " ").concat(months[0].year), "".concat(months[1].name, " ").concat(months[1].year)));
                        }
                        else {
                            liveAnnounce(l10n.formatText('datePicker.currentMonth.a11yLabel', months[0].name));
                        }
                    }
                }
            }
        }
    };
    Calendar.prototype.announceDateInstructions = function (message, setting) {
        liveAnnounce(message, setting, undefined, this.contentRef);
    };
    // First render up to the month after the selected month, then recursively render remaining months
    Calendar.prototype.renderUpToSelectedMonth = function (months) {
        var _a = this.props, selectionEnd = _a.selectionEnd, selectionStart = _a.selectionStart;
        var selection = selectionEnd || selectionStart;
        var indexOfSelectedMonth = 0;
        if (selection) {
            var selectedMonth_1 = selection.getMonth();
            var selectedYear_1 = selection.getFullYear();
            indexOfSelectedMonth = months.findIndex(function (m) { return m.month === selectedMonth_1 && m.year === selectedYear_1; });
            this.setState({ months: months.slice(0, indexOfSelectedMonth + 2) });
            this.scrollToSelection();
        }
        this.recursivelyRenderRemainingMonths(months, indexOfSelectedMonth + 1);
    };
    Calendar.prototype.render = function () {
        var _this = this;
        var _a = this.props, buttonText = _a.buttonText, className = _a.className, datePickerContent = _a.datePickerContent, endDate = _a.endDate, firstDayOfWeek = _a.firstDayOfWeek, holidays = _a.holidays, isDialog = _a.isDialog, l10n = _a.l10n, nextPageAnalytics = _a.nextPageAnalytics, onChangeEndDate = _a.onChangeEndDate, onChangeStartDate = _a.onChangeStartDate, onScrollHandler = _a.onScrollHandler, previousPageAnalytics = _a.previousPageAnalytics, selection = _a.selection, singleMode = _a.singleMode, skipToDoneButton = _a.skipToDoneButton, startDate = _a.startDate, updateDatePicker = _a.updateDatePicker;
        var months = this.state.months;
        var monthContainerClasses = getClassNameList({
            'uitk-date-picker-menu-months-container': !isDialog
        });
        var calendarClasses = getClassNameList('uitk-calendar', className);
        var getHolidayName = holidays && holidays.length > 0 ? this.getHolidayName : undefined;
        return (React.createElement("div", { className: calendarClasses, ref: this.contentRef },
            !isDialog && (React.createElement(UitkLayoutFlex, { className: getClassNameList('uitk-date-picker-menu-pagination-container'), justifyContent: "space-between" },
                React.createElement(UitkLayoutFlexItem, null,
                    React.createElement(UitkPagingPrevButton, { disabled: this.isPrevDisabled, iconId: "prevMonth", iconTitle: l10n.formatText('datePicker.previousMonth'), onClick: this.prevMonth, analytics: previousPageAnalytics, "data-stid": "date-picker-paging" })),
                React.createElement(UitkLayoutFlexItem, null,
                    React.createElement(UitkPagingNextButton, { disabled: this.isNextDisabled, iconId: "nextMonth", iconTitle: l10n.formatText('datepicker.nextMonth'), onClick: this.nextMonth, analytics: nextPageAnalytics, "data-stid": "date-picker-paging" })))),
            React.createElement("div", { className: monthContainerClasses }, months.map(function (monthInfo, index) { return (React.createElement(React.Fragment, { key: "".concat(monthInfo.name, "-").concat(index) },
                React.createElement(UitkMonth, __assign({}, monthInfo, { datePickerContent: datePickerContent, getHolidayName: getHolidayName, minDate: startDate, maxDate: endDate, key: "".concat(monthInfo.year, "-").concat(monthInfo.month), selection: _this.getSelection(monthInfo, selection), updateDatePicker: updateDatePicker, firstDayOfWeek: firstDayOfWeek, onSelectDate: _this.onSelectDate, singleMode: singleMode, onChangeStartDate: onChangeStartDate, onChangeEndDate: onChangeEndDate, buttonText: buttonText, isDialog: isDialog, onScrollHandler: onScrollHandler })),
                months.length - 1 > index && skipToDoneButton && skipToDoneButton())); }))));
    };
    return Calendar;
}(React.Component));
var UitkCalendar = function (props) {
    var sharedUIL10n = useLocalization();
    return React.createElement(Calendar, __assign({ l10n: sharedUIL10n }, props));
};
var UitkNewCalendar = UitkCalendar;

export { UitkCalendar, UitkNewCalendar };
